import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { TerminalShell } from "../components/terminal/terminal-shell";
import { openWorkspaceTab } from "../components/terminal/tabs-store";
import { createWorkspace, putWorkspaceLayout } from "../lib/workspaces";

export const Route = createFileRoute("/")({
  component: HomePage,
  validateSearch: (search: Record<string, unknown>): { symbol?: string } => ({
    symbol:
      typeof search.symbol === "string" && search.symbol.trim()
        ? search.symbol.trim().toUpperCase()
        : undefined,
  }),
});

/**
 * The terminal home. Renders the tabbed workspace shell; a `?symbol=<TICKER>`
 * deep link (the command palette's securities shortcut) seeds a fresh
 * ephemeral canvas tab for that symbol.
 */
function HomePage() {
  const { symbol } = useSearch({ from: "/" });
  return (
    <>
      {symbol ? <SymbolDeepLink symbol={symbol} /> : null}
      <TerminalShell />
    </>
  );
}

/**
 * Seed + open an ephemeral canvas for a deep-linked symbol. Best-effort: if
 * the workspace cannot be created (e.g. signed out) the shell's empty state
 * still renders.
 */
function SymbolDeepLink({ symbol }: { symbol: string }) {
  const seeded = useRef<string | null>(null);

  useEffect(() => {
    // Guard against StrictMode's double-invoke; the tab open is idempotent.
    if (seeded.current === symbol) return;
    seeded.current = symbol;
    void (async () => {
      try {
        const workspace = await createWorkspace({
          kind: "canvas",
          rigidity: "canvas_grid",
          title: symbol,
          ephemeral: true,
          expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        });
        await putWorkspaceLayout(workspace.workspaceId, [
          {
            widgetType: "price_chart",
            widgetVersion: 1,
            x: 40,
            y: 40,
            w: 640,
            h: 360,
            zIndex: 1,
            props: { symbol },
          },
        ]);
        openWorkspaceTab(workspace.workspaceId, {
          title: symbol,
          kind: "canvas",
          ephemeral: true,
        });
      } catch {
        /* deep-link seeding is best-effort */
      }
    })();
  }, [symbol]);

  return null;
}
